import { useEffect, useState } from "react";
import { X, Zap, Pause, Play, StopCircle, RefreshCw, AlertTriangle, Settings } from "lucide-react";
import api from "@/lib/api";
import { usePlant } from "@/lib/context";

const ANOMALIES = [
  { key: "wet_charge", label: "Wet Charge", controls: [
    { name: "moisture_pct", label: "Moisture %", type: "slider", min: 10, max: 30, step: 1, default: 20 },
  ]},
  { key: "contam_scrap", label: "Contaminated Scrap", controls: [
    { name: "pct", label: "Contam % of charge", type: "slider", min: 5, max: 40, step: 1, default: 20 },
  ]},
  { key: "high_steel", label: "High Steel Scrap Ratio", controls: [
    { name: "pct", label: "Steel scrap %", type: "slider", min: 30, max: 60, step: 1, default: 45 },
  ]},
  { key: "holding_event", label: "Furnace Holding (DISA Delay)", controls: [
    { name: "duration_min", label: "Delay (min)", type: "slider", min: 15, max: 90, step: 5, default: 30 },
    { name: "furnace", label: "Affected", type: "select", options: ["F1", "F2", "Both"], default: "Both" },
  ]},
  { key: "ext_t2t", label: "Extended Tap-to-Tap (Operator)", controls: [
    { name: "extra_min", label: "Additional min over baseline", type: "slider", min: 5, max: 20, step: 1, default: 10 },
  ]},
  { key: "refractory", label: "Refractory Degradation", controls: [
    { name: "severity", label: "Severity", type: "select", options: ["Mild", "Moderate", "Severe"], default: "Moderate" },
  ]},
  { key: "pf_deterioration", label: "Power Factor Deterioration", controls: [
    { name: "target_pf", label: "Target PF", type: "slider", min: 0.70, max: 0.85, step: 0.01, default: 0.78 },
  ]},
  { key: "ca_leak", label: "Compressed Air Leak", controls: [
    { name: "size", label: "Leak Size", type: "select", options: ["Small", "Medium", "Large"], default: "Medium" },
  ]},
  { key: "ca_overpressure", label: "Compressed Air Overpressure", controls: [
    { name: "setpoint_bar", label: "Setpoint (bar)", type: "slider", min: 7.0, max: 9.5, step: 0.1, default: 8.5 },
  ]},
  { key: "yield_drop", label: "Yield Deterioration", controls: [
    { name: "target_yield", label: "Target yield %", type: "slider", min: 55, max: 70, step: 1, default: 62 },
  ]},
];

function defaultParams(controls) {
  const o = {};
  controls.forEach((c) => { o[c.name] = c.default; });
  return o;
}

function ControlInput({ control, value, onChange, anomKey }) {
  if (control.type === "slider") {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="label-small">{control.label}</span>
          <span className="font-mono text-sm text-[#00E5FF]" data-testid={`anomaly-${anomKey}-${control.name}-value`}>
            {typeof value === "number" ? value : control.default}
          </span>
        </div>
        <input
          type="range"
          min={control.min}
          max={control.max}
          step={control.step}
          value={value ?? control.default}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full"
          data-testid={`anomaly-${anomKey}-${control.name}-slider`}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="label-small">{control.label}</span>
      <select
        value={value || control.default}
        onChange={(e) => onChange(e.target.value)}
        className="input-field"
        data-testid={`anomaly-${anomKey}-${control.name}-select`}
      >
        {control.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function AnomalyCard({ anomaly, isActive, onInject, onClear }) {
  const [params, setParams] = useState(() => defaultParams(anomaly.controls));
  return (
    <div className="panel p-3" data-testid={`anomaly-card-${anomaly.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display uppercase tracking-wider font-semibold text-[13px]">
          {anomaly.label}
        </div>
        {isActive && <span className="badge-status red">Injected</span>}
      </div>
      <div className="flex flex-col gap-2">
        {anomaly.controls.map((c) => (
          <ControlInput
            key={c.name}
            control={c}
            value={params[c.name]}
            onChange={(v) => setParams({ ...params, [c.name]: v })}
            anomKey={anomaly.key}
          />
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button
          className="btn warning flex-1 flex items-center justify-center gap-1"
          onClick={() => onInject(anomaly.key, params)}
          data-testid={`anomaly-${anomaly.key}-inject-btn`}
        >
          <Zap size={13} /> Inject
        </button>
        {isActive && (
          <button
            className="btn flex items-center justify-center gap-1"
            onClick={() => onClear(anomaly.key)}
            data-testid={`anomaly-${anomaly.key}-clear-btn`}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPanel({ open, onClose }) {
  const { state, refresh } = usePlant();
  const [tab, setTab] = useState("sim");
  const [resetText, setResetText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const activeKeys = new Set((state?.active_anomalies || []).map((a) => a.key));

  const injectAnomaly = async (key, params) => {
    setBusy(true);
    try { await api.post("/admin/anomaly/inject", { key, params }); refresh(); }
    finally { setBusy(false); }
  };

  const clearAnomaly = async (key) => {
    setBusy(true);
    try { await api.post(`/admin/anomaly/clear?key=${key}`); refresh(); }
    finally { setBusy(false); }
  };

  const clearAll = async () => {
    setBusy(true);
    try { await api.post(`/admin/anomaly/clear?key=all`); refresh(); }
    finally { setBusy(false); }
  };

  const simAction = async (action) => {
    setBusy(true);
    try { await api.post(`/admin/simulation/${action}`); refresh(); }
    finally { setBusy(false); }
  };

  const softReset = async () => {
    setBusy(true);
    try { await api.post("/admin/reset/soft"); refresh(); }
    finally { setBusy(false); }
  };

  const factoryReset = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/reset/factory?confirm_plant_name=${encodeURIComponent(resetText)}`);
      refresh(); setResetText("");
    } catch (e) {
      alert(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-panel">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-[520px] max-w-full bg-[#0C0D12] border-l border-[#1F212A] flex flex-col">
        <div className="panel-header" style={{ borderBottom: "1px solid #1F212A" }}>
          <div className="flex items-center gap-2">
            <Settings size={14} />
            <span>Administrator Panel</span>
          </div>
          <button onClick={onClose} data-testid="admin-close-btn"><X size={16} /></button>
        </div>
        <div className="flex border-b border-[#1F212A]">
          {[
            { id: "sim", label: "Simulation" },
            { id: "anomalies", label: "Anomalies" },
            { id: "reset", label: "Reset" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex-1 px-3 py-3 font-display uppercase tracking-wider text-xs ${tab === t.id ? "text-[#00E5FF] border-b-2 border-[#00E5FF]" : "text-[#8F93A2]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
          {tab === "sim" && (
            <div className="space-y-4">
              <div className="panel p-4">
                <div className="label-small mb-2">Mode</div>
                <div className="metric-mid text-[#00E5FF]">{state?.mode}</div>
                <div className="text-xs text-[#8F93A2] mt-1">
                  Sim running: {state?.sim_running ? "Yes" : "No"} · Speed: {state?.sim_speed}x
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button className="btn primary flex items-center justify-center gap-2" onClick={() => simAction("start")} disabled={busy} data-testid="sim-start-btn"><Play size={14} /> Start</button>
                <button className="btn flex items-center justify-center gap-2" onClick={() => simAction("pause")} disabled={busy} data-testid="sim-pause-btn"><Pause size={14} /> Pause</button>
                <button className="btn flex items-center justify-center gap-2" onClick={() => simAction("resume")} disabled={busy} data-testid="sim-resume-btn"><Play size={14} /> Resume</button>
                <button className="btn danger flex items-center justify-center gap-2" onClick={() => simAction("stop")} disabled={busy} data-testid="sim-stop-btn"><StopCircle size={14} /> Stop</button>
              </div>
              <div className="text-xs text-[#8F93A2] leading-relaxed">
                Simulation switches all L0 sensors to physics-generated data. AI models receive
                the same L3 outputs and have no visibility into anomaly injection — they detect
                and diagnose from the readings alone.
              </div>
            </div>
          )}

          {tab === "anomalies" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-small">{state?.active_anomalies?.length || 0} active</div>
                <button className="btn flex items-center gap-1" onClick={clearAll} disabled={busy} data-testid="clear-all-anomalies-btn">
                  <RefreshCw size={13} /> Clear All
                </button>
              </div>
              {ANOMALIES.map((a) => (
                <AnomalyCard
                  key={a.key}
                  anomaly={a}
                  isActive={activeKeys.has(a.key)}
                  onInject={injectAnomaly}
                  onClear={clearAnomaly}
                />
              ))}
            </div>
          )}

          {tab === "reset" && (
            <div className="space-y-4">
              <div className="panel p-4">
                <div className="font-display uppercase tracking-wider text-sm mb-2">Soft Reset</div>
                <p className="text-xs text-[#8F93A2] mb-3 leading-relaxed">
                  Clears all active anomalies and returns simulation physics to baseline.
                  Does NOT delete historian, users, or configuration.
                </p>
                <button className="btn primary w-full" onClick={softReset} disabled={busy} data-testid="soft-reset-btn">
                  Reset Simulation
                </button>
              </div>

              <div className="panel p-4 border-[#FF3333]">
                <div className="flex items-center gap-2 mb-2 text-[#FF3333]">
                  <AlertTriangle size={16} />
                  <div className="font-display uppercase tracking-wider text-sm">Factory Reset</div>
                </div>
                <p className="text-xs text-[#8F93A2] mb-3 leading-relaxed">
                  Deletes ALL historian data, AI weights, configuration. Used only before
                  redeployment to a new plant. Type the plant name to confirm:
                </p>
                <input
                  type="text"
                  className="input-field mb-2"
                  placeholder='Type: "Ergoniq OIM-Foundry"'
                  value={resetText}
                  onChange={(e) => setResetText(e.target.value)}
                  data-testid="factory-reset-confirm-input"
                />
                <button
                  className="btn danger w-full"
                  onClick={factoryReset}
                  disabled={busy || resetText.trim().toLowerCase() !== "ergoniq oim-foundry"}
                  data-testid="factory-reset-btn"
                >
                  Confirm Factory Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
